import { Link } from '@tanstack/react-router';
import { ChevronRight, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ChannelChip, DateChip } from '~/components/common/chips';
import { DryRunBadge } from '~/features/deliveries/components/chips/dry-run-badge';

import type { BatchHeader } from '../../types/catalog-operations.types';

interface Props {
  header: BatchHeader;
  batchId: string;
  /** True when any product on the current rail page delivered as a MOCK run —
   *  badges the whole page so a dry run is never mistaken for a real delivery. */
  anyMockRows: boolean;
}

/** Breadcrumb + identity header for the compare page — batch name, channel,
 *  delivered-at date. Mirrors the batch detail header's breadcrumb style. */
export function DeliveryCompareHeader({ header, batchId, anyMockRows }: Props) {
  const { t } = useTranslation('catalog-operations');
  return (
    <div className="space-y-3" data-testid="delivery-compare-header">
      <nav className="flex flex-wrap items-center gap-1.5 text-sm">
        <Link
          data-testid="delivery-compare-breadcrumb-root-link"
          to="/catalog-operations"
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 transition-colors"
        >
          <Zap className="h-4 w-4 text-amber-500" aria-hidden="true" />
          <span>{t('deliveryCompare.header.breadcrumbRoot')}</span>
        </Link>
        <ChevronRight className="text-muted-foreground h-4 w-4" aria-hidden="true" />
        <Link
          to="/catalog-operations/$batchId"
          params={{ batchId }}
          data-testid="delivery-compare-breadcrumb-batch-link"
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          {header.name.trim() !== '' ? header.name : header.batchId}
        </Link>
        <ChevronRight className="text-muted-foreground h-4 w-4" aria-hidden="true" />
        <span className="font-semibold">{t('deliveryCompare.header.breadcrumbCurrent')}</span>
      </nav>

      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-2xl font-bold tracking-tight" data-testid="delivery-compare-title">
          {t('deliveryCompare.header.title')}
        </h1>
        <ChannelChip
          platform={header.sourcePlatform}
          label={header.source}
          testId="delivery-compare-channel-chip"
        />
        {anyMockRows ? <DryRunBadge testId="delivery-compare-header-dry-run-badge" /> : null}
        {header.deliveredAt ? (
          <DateChip iso={header.deliveredAt} testId="delivery-compare-delivered-at-chip" />
        ) : null}
      </div>
      <p className="text-muted-foreground max-w-2xl text-sm">
        {t('deliveryCompare.header.description')}
      </p>
    </div>
  );
}
